import { create } from 'zustand'
import { api, type Collection } from '../api/client'
import { hydrateAllForHandle } from '../utils/hydrate'

const ALL_COLLECTIONS: Collection[] = [
  'app.bsky.feed.post',
  'app.bsky.feed.repost',
  'app.bsky.feed.like',
  'app.bsky.graph.follow',
  'app.bsky.graph.block',
  'app.bsky.graph.list',
  'app.bsky.graph.starterpack',
  'app.bsky.feed.generator',
]

export type CollectionProgress = {
  fetched: number
  pages: number
  done: boolean
  /** Cancelled but resumable — cursor is saved on the row so resumeCollection
   *  can pick up exactly where the user paused. */
  paused: boolean
  cursor: string | null
  error: string | null
}

export type PullState = {
  handle: string | null
  /** Derived convenience: any collection in-flight OR hydration running. */
  running: boolean
  startedAt: number | null
  /** When everything settles (all collections done/paused/errored AND
   *  hydration done). Used to keep the panel visible briefly so the user
   *  sees totals after a run. */
  finishedAt: number | null
  byCollection: Record<Collection, CollectionProgress>
  /** Hydration phase that runs after every collection completes — fetches
   *  AppView data for cached like/repost subjects and extracts media +
   *  sensitivity labels so the gallery is instantly accurate. */
  hydration: {
    running: boolean
    processed: number
    total: number
  }
}

const blank = (handle: string | null = null): PullState => ({
  handle,
  running: false,
  startedAt: null,
  finishedAt: null,
  byCollection: Object.fromEntries(
    ALL_COLLECTIONS.map((c) => [
      c,
      { fetched: 0, pages: 0, done: false, paused: false, cursor: null, error: null },
    ]),
  ) as Record<Collection, CollectionProgress>,
  hydration: { running: false, processed: 0, total: 0 },
})

// Module-level scratch — the AbortControllers can't live in zustand state
// (they're not serializable, and state updates would clone them). The
// `running` flag in state is recomputed from inflight.size + hydration on
// every relevant set so React components see correct values.
const inflight = new Map<Collection, AbortController>()
let hydrationAbort: AbortController | null = null

type SetFn = (
  fn: (s: { state: PullState; visible: boolean }) => Partial<{ state: PullState; visible: boolean }>,
) => void

function withRunning(s: PullState): PullState {
  return { ...s, running: inflight.size > 0 || s.hydration.running }
}

async function walkCollection(
  set: SetFn,
  handle: string,
  collection: Collection,
  service: string,
  accessJwt: string | undefined,
  startCursor: string | null,
): Promise<void> {
  const controller = new AbortController()
  inflight.set(collection, controller)
  set((s) => ({
    state: withRunning({
      ...s.state,
      byCollection: {
        ...s.state.byCollection,
        [collection]: {
          ...s.state.byCollection[collection],
          paused: false,
          error: null,
        },
      },
    }),
  }))

  let cursor: string | undefined = startCursor ?? undefined
  let pageNumber = 0

  try {
    while (true) {
      if (controller.signal.aborted) break

      let attempt = 0
      let page: Awaited<ReturnType<typeof api.page>> | null = null
      while (true) {
        try {
          page = await api.page({
            handle,
            collection,
            cursor,
            limit: pageNumber === 0 ? 50 : 100,
            service,
            accessJwt,
            signal: controller.signal,
          })
          break
        } catch (err) {
          if (
            controller.signal.aborted ||
            (err instanceof DOMException && err.name === 'AbortError')
          ) {
            page = null
            break
          }
          attempt++
          if (attempt >= 4) {
            set((s) => ({
              state: withRunning({
                ...s.state,
                byCollection: {
                  ...s.state.byCollection,
                  [collection]: {
                    ...s.state.byCollection[collection],
                    error: err instanceof Error ? err.message : String(err),
                  },
                },
              }),
            }))
            page = null
            break
          }
          await new Promise((r) => setTimeout(r, Math.min(750 * 2 ** attempt, 30_000)))
        }
      }

      if (!page) break // aborted or hard error
      const fetchedThisPage = page.records.length
      pageNumber += 1
      cursor = page.cursor ?? undefined

      set((s) => ({
        state: withRunning({
          ...s.state,
          byCollection: {
            ...s.state.byCollection,
            [collection]: {
              ...s.state.byCollection[collection],
              fetched: s.state.byCollection[collection].fetched + fetchedThisPage,
              pages: s.state.byCollection[collection].pages + 1,
              cursor: cursor ?? null,
              done: !cursor,
              paused: false,
              error: null,
            },
          },
        }),
      }))

      if (!cursor) break
    }
  } finally {
    inflight.delete(collection)
    set((s) => {
      const cur = s.state.byCollection[collection]
      // Aborted mid-walk → paused. Otherwise leave done/error as set above.
      const paused = controller.signal.aborted && !cur.done
      return {
        state: withRunning({
          ...s.state,
          byCollection: {
            ...s.state.byCollection,
            [collection]: { ...cur, paused },
          },
        }),
      }
    })
  }
}

type PullAll = {
  state: PullState
  visible: boolean
  show: () => void
  hide: () => void
  /** Wipe per-collection progress and walk every collection from the top.
   *  Triggers the post-walk hydration when all collections finish cleanly. */
  pull: (handle: string, service: string, accessJwt?: string) => Promise<void>
  /** Pause one collection — its cursor stays in state so resumeCollection
   *  picks up exactly where it left off. */
  cancelCollection: (collection: Collection) => void
  /** Resume a paused collection from its saved cursor. No-op if it's
   *  already running, done, or has no cursor saved. */
  resumeCollection: (
    collection: Collection,
    service: string,
    accessJwt?: string,
  ) => Promise<void>
  /** Cancel every in-flight collection AND the hydration phase. */
  cancel: () => void
  reset: () => void
}

export const usePullAll = create<PullAll>((set, get) => ({
  state: blank(),
  visible: false,
  show: () => set({ visible: true }),
  hide: () => set({ visible: false }),

  cancelCollection: (collection) => {
    const ctrl = inflight.get(collection)
    if (ctrl) ctrl.abort()
    // walk's finally block flips `paused: true` once it exits.
  },

  resumeCollection: async (collection, service, accessJwt) => {
    if (inflight.has(collection)) return
    const cur = get().state
    const handle = cur.handle
    if (!handle) return
    const collState = cur.byCollection[collection]
    if (collState.done) return
    set({ visible: true })
    await walkCollection(set, handle, collection, service, accessJwt, collState.cursor)
  },

  cancel: () => {
    for (const ctrl of inflight.values()) ctrl.abort()
    if (hydrationAbort) hydrationAbort.abort()
    set((s) => ({
      state: withRunning({
        ...s.state,
        hydration: { ...s.state.hydration, running: false },
        finishedAt: performance.now(),
      }),
    }))
  },

  reset: () => {
    inflight.clear()
    if (hydrationAbort) hydrationAbort.abort()
    hydrationAbort = null
    set({ state: blank(), visible: false })
  },

  pull: async (handle, service, accessJwt) => {
    if (inflight.size > 0 || get().state.hydration.running) return
    set({
      state: { ...blank(handle), running: true, startedAt: performance.now() },
      visible: true,
    })

    await Promise.all(
      ALL_COLLECTIONS.map((c) => walkCollection(set, handle, c, service, accessJwt, null)),
    )

    // Only run hydration if every collection completed cleanly. Paused or
    // errored collections mean the cache is incomplete; hydration would
    // burn AppView quota on partial data, so skip until the user resumes.
    const allDone = ALL_COLLECTIONS.every((c) => {
      const cs = get().state.byCollection[c]
      return cs.done && !cs.error
    })
    if (allDone) {
      hydrationAbort = new AbortController()
      set((s) => ({
        state: withRunning({
          ...s.state,
          hydration: { running: true, processed: 0, total: 0 },
        }),
      }))
      try {
        await hydrateAllForHandle(handle, {
          signal: hydrationAbort.signal,
          onProgress: (processed, total) => {
            set((s) => ({
              state: withRunning({
                ...s.state,
                hydration: { running: true, processed, total },
              }),
            }))
          },
        })
      } catch {
        /* hydration errors are non-fatal — partial cache is still useful */
      }
      set((s) => ({
        state: withRunning({
          ...s.state,
          hydration: { ...s.state.hydration, running: false },
        }),
      }))
      hydrationAbort = null
    }

    set((s) => ({
      state: withRunning({ ...s.state, finishedAt: performance.now() }),
    }))
  },
}))

export const PULL_COLLECTIONS = ALL_COLLECTIONS
